import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import 'react-image-lightbox/style.css';
import { useStyles, WhiteBackgroundCheckbox } from './Images.styled';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import ImageList from '@material-ui/core/ImageList';
import ImageListItem from '@material-ui/core/ImageListItem';
import PublishIcon from '@material-ui/icons/Publish';
import Lightbox from 'react-image-lightbox';
import { Card } from 'components/UI';
import { Button } from '@material-ui/core';
import { UploadImageModal } from 'components/Modals';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { BaseModal } from 'components/UI';
import i18n from 'i18n-js';
import { FilesActions } from 'state/modules/files';
import { useDispatch, useSelector } from 'react-redux';

window.global = window;

export const Images = ({ id, width, title, sourceType, noProfile = false }) => {
  const dispatch = useDispatch();

  const classes = useStyles();
  const [file, setFile] = useState('');
  const [openImageModal, setOpenImageModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [imagesSelected, setImagesSelected] = useState({});
  const [lightboxProperties, setLightboxProperties] = useState({
    isOpen: false,
  });

  const {
    fetchedUploadImageSuccess,
    fetchingUploadImage,
    images,
    fetchedUpdateImageSuccess,
    fetchedDeleteImageSuccess,
  } = useSelector((state) => state.files);

  const setProfileImage = (payload) => {
    dispatch(FilesActions.fetchUpdateImage(payload));
  };

  const uploadImage = (payload) => {
    dispatch(FilesActions.fetchUploadImage(payload));
  };

  const deleteImages = () => {
    dispatch(
      FilesActions.fetchDeleteImage({
        image_id: Object.keys(imagesSelected),
        source_type: sourceType,
        source_id: id,
      })
    );
    setImagesSelected({});
    handleOpenDeleteModal();
    document.getElementById('imageUploadInput').value = '';
  };

  const onReset = () => {
    dispatch(FilesActions.resetUploadFileSuccess());
  };

  /**
   * Handle Menu
   * @param {string=[profile, delete]} action Action name
   */
  const handleMenu = (action) => {
    switch (action) {
      // set profile image
      case 'profile':
        const payload = {
          image_id: Object.keys(imagesSelected)[0],
          is_default: true,
          source_id: id,
          source_type: sourceType,
        };
        setProfileImage(payload);
        break;
      // delete image
      case 'delete':
        handleOpenDeleteModal();
        break;

      default:
        break;
    }
  };

  /**
   * Handle Checkbox Change
   * @param {object} event Checkbox event
   * @param {string} name Checkbox name
   */
  const handleCheckboxChange = (event, data) => {
    var image = { ...imagesSelected };
    if (event.target.checked) {
      image[data?.id] = data;
    }
    // if not selected it is removed from the variable
    else {
      delete image[data?.id];
    }
    setImagesSelected(image);
  };

  const fetchImages = () => {
    dispatch(
      FilesActions.fetchImages({
        source_type: sourceType,
        source_id: id,
      })
    );
  };

  useEffect(() => {
    if (id && (fetchedUploadImageSuccess || fetchedDeleteImageSuccess)) {
      fetchImages();
      document.getElementById('imageUploadInput').value = '';
    }
  }, [id, fetchedUploadImageSuccess, fetchedDeleteImageSuccess]); // eslint-disable-line

  useEffect(() => {
    if (id && fetchedUpdateImageSuccess) {
      fetchImages();
      setImagesSelected({});
    }
  }, [id, fetchedUpdateImageSuccess]); // eslint-disable-line

  useEffect(() => {
    if (id) {
      fetchImages();
    }
  }, [id]); // eslint-disable-line

  /**
   * Handle Image Change
   * @param {object} event Files input event
   */
  const handleImageChange = (event) => {
    event.preventDefault();
    // get images from event object
    const file = event.target.files[0];
    // verify if an image is selected
    if (file) {
      setFile(event.target.files[0]);
      // open modal with selected image
      setOpenImageModal(true);
    }
  };
  /**
   * Handle Image Modal
   */
  const handleImageModal = () => {
    // open or close image modal
    setOpenImageModal((prevState) => !prevState);
  };
  /**
   * Get grid list cols
   */
  const getGridListCols = () => {
    if (isWidthUp('xl', width)) {
      return 4;
    }

    if (isWidthUp('lg', width)) {
      return 4;
    }

    if (isWidthUp('md', width)) {
      return 3;
    }

    return 2;
  };

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal((prevState) => !prevState);
  };

  const getImagesNames = () => {
    const imgs = Object.keys(imagesSelected)
      .map((key) => imagesSelected[key].name)
      ?.toString()
      .replace(',', ', ');
    return imgs;
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    ...(noProfile
      ? []
      : [
          {
            title: i18n.t('common.set_profile_image'),
            disabled: Object.keys(imagesSelected).length !== 1,
            onClick: () => handleMenu('profile'),
          },
        ]),
    {
      title: i18n.t('common.delete'),
      onClick: () => handleMenu('delete'),
    },
  ];

  return (
    <Card
      title={title}
      menuItems={Object.keys(imagesSelected).length ? items : []}
      datacy="images-widget"
      headerActionType="vertical"
    >
      <Box className={classes.container}>
        <Grid container direction="row" alignItems="center" spacing={2} className={classes.mt5}>
          <Grid item xs={12}>
            <div className={classes.rootList}>
              <ImageList
                gap={8}
                rowHeight={100}
                className={classes.gridList}
                cols={getGridListCols()}
              >
                <ImageListItem classes={{ item: classes.uploadImage }} cols={1}>
                  <Button
                    variant="contained"
                    component="label"
                    classes={{
                      root: classes.uploadFile,
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      textAlign="center"
                      justifyContent="center"
                      height="100%"
                      width="100%"
                    >
                      <span className={classes.uploadText}>
                        <PublishIcon />
                      </span>
                      <Typography className={classes.uploadText} variant="body2" component={'span'}>
                        <Box fontSize={12}>{i18n.t('common.upload_image')}</Box>
                      </Typography>
                      <input
                        type="file"
                        id="imageUploadInput"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </Box>
                  </Button>
                </ImageListItem>
                {images.map((item, index) => (
                  <ImageListItem
                    classes={{ imgFullHeight: classes.images }}
                    className={classes.imagesEvent}
                    key={item.id}
                    cols={1}
                  >
                    <img
                      src={item.thumbnail_url}
                      data-cy={`image-item-${index}`}
                      alt={item.name}
                      onClick={() => {
                        setLightboxProperties({
                          isOpen: true,
                          photoIndex: index,
                        });
                      }}
                    />
                    {item?.is_type_default && (
                      <div className={classes.profileImage}>
                        <span> {i18n.t('common.profile_image')} </span>
                      </div>
                    )}
                    <WhiteBackgroundCheckbox
                      size="small"
                      checked={imagesSelected[item.id] ? true : false}
                      className={'checkboxItem'}
                      onChange={(e) => handleCheckboxChange(e, item)}
                      color="primary"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </div>
            {/* show lightbox when view document */}
            {lightboxProperties.isOpen && (
              <div>
                <Lightbox
                  data-cy="lightbox"
                  imageTitle={images[lightboxProperties.photoIndex]?.name}
                  mainSrc={images[lightboxProperties.photoIndex]?.url_path}
                  nextSrc={images[(lightboxProperties.photoIndex + 1) % images.length]?.url_path}
                  prevSrc={
                    images[(lightboxProperties.photoIndex + images.length - 1) % images.length]
                      ?.url_path
                  }
                  onCloseRequest={() =>
                    setLightboxProperties({
                      isOpen: false,
                    })
                  }
                  onMovePrevRequest={() =>
                    setLightboxProperties({
                      ...lightboxProperties,
                      photoIndex:
                        (lightboxProperties.photoIndex + images.length - 1) % images.length,
                    })
                  }
                  onMoveNextRequest={() =>
                    setLightboxProperties({
                      isOpen: true,
                      photoIndex: (lightboxProperties.photoIndex + 1) % images.length,
                    })
                  }
                  reactModalStyle={{
                    overlay: {
                      zIndex: '1300',
                    },
                  }}
                />
              </div>
            )}
            {openImageModal && (
              <UploadImageModal
                id={id}
                open={true}
                file={file}
                handleClick={handleImageModal}
                onUploadImage={uploadImage}
                onReset={onReset}
                data={{
                  source_type: sourceType,
                  source_id: id,
                }}
                loadingSuccess={fetchedUploadImageSuccess}
                loadingUpload={fetchingUploadImage}
              />
            )}
            {openDeleteModal && (
              <BaseModal
                size="sm"
                open={openDeleteModal}
                hasEnterButton
                hasCancelButton
                enableCloseButton={false}
                title={i18n.t('wizard.are_you_sure')}
                enterButtonLabel={i18n.t('common.yes')}
                children={`${i18n.t('vehicle_types.delete_confirm')}: ${getImagesNames()}?`}
                onEnter={deleteImages}
                onCancel={handleOpenDeleteModal}
                handleClick={handleOpenDeleteModal}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};
Images.propTypes = {
  id: PropTypes.any,
  images: PropTypes.array,
};

Images.defaultProps = {
  id: '',
  images: [],
};
export default withWidth()(Images);
